import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import carolPortrait from './assets/carol-west-enhanced.png';
import brandLogo from './assets/carol-west-homes-logo.png';
import soldHomesFallback from './data/soldHomes.json';
import './App.css';

ReactGA.initialize('G-LS4TH1KN27');
ReactGA.send({ hitType: 'pageview', page: window.location.pathname, title: document.title });

const hostname = window.location.hostname;
const initialFormState = {
  name: '', email: '', phone: '', goal: 'Buying a home',
  timeline: 'As soon as possible', area: '', message: '', emailid: 22,
  responder: 71, segment: 25, emailContactId: 294813, domain: hostname,
};
const successMessage = 'Thanks. Carol will review your request and follow up shortly.';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const soldHomesSheetId = '1rGhp6Ep6fESYIBVyHdK40MYgt4_-k9ljVa9_zjhakVk';
const soldHomesSheetGid = '0';
const soldHomesRefreshInterval = 60000;
const recaptchaAction = 'lead_submission';
let recaptchaScriptPromise;

const loadRecaptcha = (siteKey) => {
  if (!siteKey) {
    return Promise.reject(new Error('Form protection is not configured. Please call Carol directly.'));
  }
  if (window.grecaptcha) return Promise.resolve(window.grecaptcha);
  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}&trustedtypes=true`;
    script.async = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => {
      recaptchaScriptPromise = undefined;
      script.remove();
      reject(new Error('Unable to load form protection. Please refresh and try again.'));
    };
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
};

const getRecaptchaToken = async () => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const grecaptcha = await loadRecaptcha(siteKey);

  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action: recaptchaAction }).then(resolve).catch(reject);
    });
  });
};

const homesFromGoogleTable = (table) => {
  const headers = (table?.cols || []).map((column) =>
    String(column.label || column.id || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  );
  const findColumn = (aliases) => headers.findIndex((header) => aliases.includes(header));
  const columnIndexes = {
    address: findColumn(['address', 'streetaddress', 'propertyaddress']),
    city: findColumn(['city', 'market', 'location']),
    price: findColumn(['price', 'soldprice', 'saleprice', 'closedprice']),
    detail: findColumn(['detail', 'details', 'propertydetails']),
    beds: findColumn(['beds', 'bedrooms', 'bed']),
    baths: findColumn(['baths', 'bathrooms', 'bath']),
  };
  const cellValue = (cells, index, formatted = false) => {
    if (index < 0 || !cells[index]) return '';
    return String((formatted && cells[index].f) || cells[index].v || '').trim();
  };

  return (table?.rows || []).map(({ c: cells = [] }) => {
    const beds = cellValue(cells, columnIndexes.beds, true);
    const baths = cellValue(cells, columnIndexes.baths, true);
    const generatedDetail = [beds && `${beds} beds`, baths && `${baths} baths`].filter(Boolean).join(' · ');
    return {
      address: cellValue(cells, columnIndexes.address),
      city: cellValue(cells, columnIndexes.city),
      price: cellValue(cells, columnIndexes.price, true),
      detail: cellValue(cells, columnIndexes.detail) || generatedDetail,
    };
  }).filter((home) => home.address && home.price);
};

const loadSoldHomesFromGoogle = () => new Promise((resolve, reject) => {
  const callbackName = `westLegacySoldHomes_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const script = document.createElement('script');
  const timeout = window.setTimeout(() => finish(new Error('Google Sheets timed out.')), 8000);

  const cleanup = () => {
    window.clearTimeout(timeout);
    delete window[callbackName];
    script.remove();
  };
  const finish = (error, homes) => {
    cleanup();
    if (error) reject(error);
    else resolve(homes);
  };

  window[callbackName] = (payload) => {
    if (payload?.status !== 'ok') {
      finish(new Error('Google Sheets returned an invalid response.'));
      return;
    }
    const homes = homesFromGoogleTable(payload.table);
    finish(homes.length ? null : new Error('No valid sold-home rows were found.'), homes);
  };
  script.onerror = () => finish(new Error('Unable to reach Google Sheets.'));
  script.src = `https://docs.google.com/spreadsheets/d/${soldHomesSheetId}/gviz/tq?tqx=${encodeURIComponent(`out:json;responseHandler:${callbackName}`)}&gid=${soldHomesSheetGid}&_=${Date.now()}`;
  document.head.appendChild(script);
});

const getResponseMessage = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const payload = await response.json().catch(() => null);
    return payload?.message || '';
  }
  const text = await response.text().catch(() => '');
  return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const validateForm = (formData) => {
  if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
    return 'Please complete your name, email, and phone number.';
  }
  if (!emailPattern.test(formData.email.trim())) return 'Please enter a valid email address.';
  return '';
};

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const menuToggleRef = useRef(null);
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [soldHomes, setSoldHomes] = useState(soldHomesFallback);
  const [soldHomesSource, setSoldHomesSource] = useState('local-fallback');

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuToggleRef.current?.focus();
      }
    };
    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) setIsMenuOpen(false);
    };
    const handleResize = () => {
      if (window.innerWidth > 980) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return;
    loadRecaptcha(process.env.REACT_APP_RECAPTCHA_SITE_KEY).catch((error) => {
      console.warn(error.message);
    });
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test' || typeof fetch !== 'function') return undefined;
    const controller = new AbortController();

    const loadSoldHomes = async () => {
      const isClientOnlyDevelopment = ['localhost', '127.0.0.1'].includes(window.location.hostname)
        && window.location.port === '3000';

      if (isClientOnlyDevelopment) {
        try {
          setSoldHomes(await loadSoldHomesFromGoogle());
          setSoldHomesSource('google-sheet-direct');
        } catch (sheetError) {
          console.warn('Using local sold-home data:', sheetError.message);
        }
        return;
      }

      try {
        const response = await fetch(`/api/sold-homes?_=${Date.now()}`, {
          signal: controller.signal,
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) throw new Error('Unable to load sold homes from the site API.');
        const payload = await response.json();
        if (payload?.source !== 'google-sheet' || !Array.isArray(payload.homes) || !payload.homes.length) {
          throw new Error('The site API is using fallback data.');
        }
        setSoldHomes(payload.homes);
        setSoldHomesSource('google-sheet-api');
      } catch (apiError) {
        if (apiError.name === 'AbortError') return;
        try {
          setSoldHomes(await loadSoldHomesFromGoogle());
          setSoldHomesSource('google-sheet-direct');
        } catch (sheetError) {
          console.warn('Using local sold-home data:', sheetError.message);
        }
      }
    };

    loadSoldHomes();
    const refreshTimer = window.setInterval(loadSoldHomes, soldHomesRefreshInterval);

    return () => {
      controller.abort();
      window.clearInterval(refreshTimer);
    };
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const submittedForm = new FormData(event.currentTarget);
    const company = String(submittedForm.get('company') || '').trim();
    const domain = String(submittedForm.get('domain') || hostname).trim();
    const payload = { ...formData, domain };

    if (company) {
      setStatus({ type: 'error', message: 'Submission was blocked. Please refresh the page and try again.' });
      return;
    }
    const validationMessage = validateForm(formData);
    if (validationMessage) {
      setStatus({ type: 'error', message: validationMessage });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const recaptchaToken = await getRecaptchaToken();
      const response = await fetch('https://api.digitalwerks.com/mautic/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, recaptchaToken }),
      });
      const responseMessage = await getResponseMessage(response);
      if (!response.ok) throw new Error(responseMessage || 'Unable to submit your request right now.');
      setStatus({ type: 'success', message: successMessage });
      ReactGA.event('Lead Submitted', { domain });
      setFormData(initialFormState);
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Something went wrong. Please try again in a moment.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="site-shell">
      <header className="site-header" ref={headerRef} onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsMenuOpen(false);
      }}>
        <a className="wordmark" href="#top" aria-label="Carol West Homes home">
          <img className="brand-lockup" src={brandLogo} alt="Carol West Homes — Buy, Sell, Invest" />
        </a>
        <button className="menu-toggle" type="button" ref={menuToggleRef}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen} aria-controls="main-navigation"
          onClick={() => setIsMenuOpen((open) => !open)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d={isMenuOpen ? 'M6 6l12 12M6 18L18 6' : 'M4 6h16M4 12h16M4 18h16'} />
          </svg>
        </button>
        <nav id="main-navigation" className={isMenuOpen ? 'is-open' : ''} aria-label="Main navigation"
          onClick={(event) => {
            if (event.target.closest('a')) setIsMenuOpen(false);
          }}>
          <a href="#expertise">Expertise</a>
          <a href="#investors">Investors</a>
          <a href="#approach">Approach</a>
          <a href="#about">About</a>
          <a href="https://blog.westlegacyrealty.com">Insights</a>
        </nav>
        <a className="header-cta" href="#consultation" onClick={() => setIsMenuOpen(false)} aria-label="Start a conversation">Start a conversation <ArrowIcon /></a>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero-overlay" />
          <div className="hero-content">
            <p className="eyebrow">Cherokee County · Metro Atlanta</p>
            <h1>Cherokee County real estate guidance for <em>your next move.</em></h1>
            <p className="hero-tagline"><span />Making Moves, Together.</p>
            <p className="hero-lede">Carol West is a Cherokee County, GA real estate agent helping buyers, sellers, investors, and relocating families move with confidence from the first conversation through closing.</p>
            <div className="hero-actions">
              <a className="button button-light" href="#consultation">Plan your move <ArrowIcon /></a>
              <a className="text-link" href="#about">Meet Carol <span aria-hidden="true">↓</span></a>
            </div>
          </div>
          <div className="hero-market-note">
            <span>Serving North Georgia</span>
            <strong>Canton / Woodstock / Ball Ground / Metro Atlanta</strong>
          </div>
        </section>

        <section className="sold-ledger" data-source={soldHomesSource} aria-label="Recently sold properties">
          <div className="ledger-label"><i /> Recently sold</div>
          <div className="ticker-window">
            <div className="ticker-track">
              {[...soldHomes, ...soldHomes].map((home, index) => (
                <article className="sold-item" key={`${home.address}-${index}`} aria-hidden={index >= soldHomes.length}>
                  <span><strong>{home.address}</strong><small>{home.city}, GA · {home.detail}</small></span>
                  <b>{home.price}</b>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="intro section-wrap" id="expertise">
          <div className="section-kicker">Local knowledge. Personal strategy.</div>
          <div className="intro-copy">
            <h2>Local real estate guidance for Cherokee County buyers and sellers.</h2>
            <p>Every move carries a different set of priorities. Carol combines market experience in Canton, Woodstock, Ball Ground, Holly Springs, and surrounding North Georgia communities with straightforward communication to help you act with clarity—not pressure.</p>
          </div>
        </section>

        <section className="service-grid section-wrap" aria-label="Real estate services">
          <article className="service-card service-featured">
            <span className="service-number">01</span>
            <div><p className="card-label">For sellers</p><h3>Position your home to stand apart.</h3><p>Pricing guidance, thoughtful preparation, strategic marketing, and skilled negotiation designed around your property.</p></div>
            <a href="#consultation" aria-label="Discuss selling your home">Discuss your sale <ArrowIcon /></a>
          </article>
          <article className="service-card">
            <span className="service-number">02</span>
            <div><p className="card-label">For buyers</p><h3>Find the right home—and buy it well.</h3><p>Focused searches, neighborhood insight, offer strategy, and calm advocacy in a competitive market.</p></div>
            <a href="#consultation" aria-label="Start a home search">Start your search <ArrowIcon /></a>
          </article>
          <article className="service-card">
            <span className="service-number">03</span>
            <div><p className="card-label">For relocations</p><h3>Arrive in Georgia with a local advantage.</h3><p>On-the-ground guidance for comparing communities, commute patterns, homes, and the details that shape daily life.</p></div>
            <a href="#consultation" aria-label="Plan a relocation to Georgia">Plan your relocation <ArrowIcon /></a>
          </article>
        </section>

        <section className="investor" id="investors">
          <div className="investor-inner section-wrap">
            <div className="investor-heading">
              <p className="eyebrow dark">For real estate investors</p>
              <h2>Local insight for decisions that need to work on paper—and in practice.</h2>
              <p>From a first rental property to a growing portfolio, Carol helps investors identify opportunities, understand the property and its market, and move through the transaction with a clear plan.</p>
              <div className="investor-types" aria-label="Investment property types">
                <span>Long-term rentals</span>
                <span>Value-add properties</span>
                <span>Land</span>
                <span>Portfolio sales</span>
              </div>
              <a className="button button-dark" href="#consultation">Discuss an investment goal <ArrowIcon /></a>
            </div>

            <aside className="investor-brief" aria-label="Investor real estate support">
              <p className="card-label">An investor-minded process</p>
              <ul>
                <li><span>01</span><div><h3>Source with purpose</h3><p>Focus the search around your property criteria, target market, timeline, and intended strategy.</p></div></li>
                <li><span>02</span><div><h3>Evaluate the fundamentals</h3><p>Compare location, condition, market demand, resale considerations, and the realities behind the listing.</p></div></li>
                <li><span>03</span><div><h3>Execute the transaction</h3><p>Coordinate offers, due diligence, inspections, negotiations, and closing with responsive representation.</p></div></li>
                <li><span>04</span><div><h3>Plan the next move</h3><p>Prepare an investment property for sale or evaluate how a disposition fits your broader portfolio plans.</p></div></li>
              </ul>
              <p className="investor-note">Carol provides real estate guidance and works alongside your lending, tax, and legal professionals.</p>
            </aside>
          </div>
        </section>

        <section className="about section-wrap" id="about">
          <div className="portrait-frame">
            <div className="portrait-backdrop" />
            <img src={carolPortrait} alt="Carol West, Georgia real estate professional" />
            <span>Professional representation<br />with a personal commitment.</span>
          </div>
          <div className="about-copy">
            <p className="eyebrow dark">Your Georgia real estate advisor</p>
            <h2>Experience you can feel at every step.</h2>
            <p className="about-lede">Carol West brings local focus, responsive communication, and disciplined follow-through to each client relationship.</p>
            <p>From preparing a property for market to navigating inspections and negotiations, Carol keeps the process understandable and the next step clear. Her work is grounded in careful listening, honest counsel, and a commitment to protecting what matters to you.</p>
            <div className="about-markets"><span>Primary markets</span><strong>Cherokee County</strong><strong>North Georgia</strong><strong>Metro Atlanta</strong></div>
          </div>
        </section>

        <section className="process section-wrap" id="approach">
          <div className="process-heading"><p className="eyebrow dark">A clear way forward</p><h2>Good outcomes begin with a good plan.</h2></div>
          <ol>
            <li><span>01</span><div><h3>Listen</h3><p>Start with your goals, timing, questions, and what a successful move looks like to you.</p></div></li>
            <li><span>02</span><div><h3>Advise</h3><p>Build a practical market strategy based on local conditions and your priorities.</p></div></li>
            <li><span>03</span><div><h3>Execute</h3><p>Move from preparation to closing with attentive communication and steady representation.</p></div></li>
          </ol>
        </section>

        <section className="consultation" id="consultation">
          <div className="consultation-copy">
            <p className="eyebrow">Your next move starts here</p>
            <h2>Let’s talk about what’s ahead.</h2>
            <p>Share a few details and Carol will personally follow up to learn more about your real estate goals.</p>
            <div className="contact-promise"><span>What to expect</span><p>A private, no-pressure conversation and clear next steps matched to your timeline.</p></div>
          </div>

          <form className="lead-form" noValidate onSubmit={handleSubmit}>
            <input type="hidden" name="domain" value={formData.domain} readOnly />
            <div className="field-grid">
              <label className="field"><span>Name *</span><input type="text" name="name" placeholder="Your full name" value={formData.name} onChange={handleChange} required /></label>
              <label className="field"><span>Email *</span><input type="email" name="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required /></label>
              <label className="field"><span>Phone *</span><input type="tel" name="phone" placeholder="Best number to reach you" value={formData.phone} onChange={handleChange} required /></label>
              <label className="field"><span>I’m interested in</span><select name="goal" value={formData.goal} onChange={handleChange}><option>Buying a home</option><option>Selling a home</option><option>Buying and selling</option><option>Home value request</option><option>Investment property</option><option>Relocation help</option></select></label>
              <label className="field"><span>Timeline</span><select name="timeline" value={formData.timeline} onChange={handleChange}><option>As soon as possible</option><option>Within 30 days</option><option>Within 3 months</option><option>Within 6 months</option><option>Just researching</option></select></label>
              <label className="field"><span>Area of interest</span><input type="text" name="area" placeholder="Canton, Woodstock, Atlanta..." value={formData.area} onChange={handleChange} /></label>
              <label className="field field-full"><span>How can Carol help?</span><textarea name="message" placeholder="Tell us about your goals, property, budget, or questions." value={formData.message} onChange={handleChange} rows="4" /></label>
              <label className="bot-trap" aria-hidden="true"><span>Company</span><input type="text" name="company" tabIndex="-1" autoComplete="new-password" /></label>
            </div>
            <button className="submit-button" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Sending your request...' : <>Get My Consultation <ArrowIcon /></>}</button>
            <p className="form-note">
              By submitting, you agree to be contacted about your real estate inquiry. This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
              <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </p>
            {isSubmitting && <p className="status-message pending" role="status">Sending your request...</p>}
            {!isSubmitting && status.message && <p className={`status-message ${status.type}`} role="status">{status.message}</p>}
          </form>
        </section>
      </main>

      <footer>
        <div className="wordmark footer-mark"><img className="brand-lockup" src={brandLogo} alt="Carol West Homes — Buy, Sell, Invest" /></div>
        <div className="footer-details">
          <p><strong>Carol West, REALTOR®</strong> · Georgia license #434438</p>
          <address>Keller Williams Realty Partners · 722 Stonecroft Lane, Woodstock, GA 30188 · <a href="tel:+14043105110">(404) 310-5110</a></address>
          <p>Serving Cherokee County, North Georgia &amp; Metro Atlanta.</p>
        </div>
        <a href="#top">Back to top ↑</a>
      </footer>
    </div>
  );
}

export default App;
